/**
 * Modal shown on 👎 click. Asks the user to categorise the problem and
 * (optionally) explain it. Categories mirror the FEEDBACK_CATEGORIES enum
 * in the backend (routers/chat.py) — keep in sync.
 *
 * UX choices:
 *   - Both category AND comment are OPTIONAL — a bare 👎 is a valid signal.
 *   - Clicking outside the modal cancels (no submission).
 *   - Submit is enabled at all times so users can confirm "👎 no reason given".
 *   - On re-open (user already left feedback), the previous category +
 *     comment are pre-filled so they can revise rather than re-enter.
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { ChatMessageFeedback } from '../../stores/chatStore';
import { useT } from '../../i18n';

// Category keys mirror the FEEDBACK_CATEGORIES enum in routers/chat.py.
// Localised labels + descriptions come from i18n at render time, so the
// backend stays language-agnostic (it stores the key, not the prose).
const CATEGORY_KEYS = [
  'inaccurate',
  'incomplete',
  'wrong_citation',
  'off_topic',
  'formatting',
  'other',
] as const;

interface FeedbackDownModalProps {
  messageId: string;
  existing?: ChatMessageFeedback;
  onClose:  () => void;
  onSubmit: (payload: { category?: string; comment?: string }) => void;
}

export function FeedbackDownModal({ existing, onClose, onSubmit }: FeedbackDownModalProps) {
  const t = useT();
  const [category, setCategory] = useState<string>(existing?.category ?? '');
  const [comment,  setComment]  = useState<string>(existing?.comment ?? '');
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap + Escape-to-close + click-outside-to-close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSubmit = () => {
    onSubmit({
      category: category || undefined,
      comment:  comment.trim() || undefined,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
    >
      <div
        ref={dialogRef}
        onClick={e => e.stopPropagation()}
        className="bg-[var(--surface)] rounded-lg shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto custom-scrollbar"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <h3 id="feedback-modal-title" className="text-[14px] font-medium text-[var(--text-1)]">
            {t('chat.feedback.modal.title')}
          </h3>
          <button onClick={onClose} aria-label={t('chat.feedback.modal.cancel')} className="text-[var(--text-3)] hover:text-[var(--text-1)]">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          <p className="text-[12px] text-[var(--text-3)]">
            {t('chat.feedback.modal.hint')}
          </p>

          <fieldset>
            <legend className="text-[11px] uppercase tracking-wide text-[var(--text-3)] mb-1.5">
              {t('chat.feedback.modal.category')}
            </legend>
            <div className="grid grid-cols-1 gap-1">
              {CATEGORY_KEYS.map(key => (
                <label
                  key={key}
                  className={`flex items-start gap-2 p-2 rounded cursor-pointer border transition-colors ${
                    category === key
                      ? 'border-[var(--accent)] bg-[var(--surface-2)]'
                      : 'border-[var(--border)] hover:bg-[var(--surface-2)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="feedback-category"
                    value={key}
                    checked={category === key}
                    onChange={() => setCategory(key)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-[12px] text-[var(--text-1)] font-medium">
                      {t(`chat.feedback.cat.${key}`)}
                    </div>
                    <div className="text-[11px] text-[var(--text-3)]">
                      {t(`chat.feedback.cat.${key}.desc`)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label htmlFor="feedback-comment" className="block text-[11px] uppercase tracking-wide text-[var(--text-3)] mb-1">
              {t('chat.feedback.modal.comment')}
            </label>
            <textarea
              id="feedback-comment"
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder={t('chat.feedback.modal.commentPlaceholder')}
              className="w-full text-[12px] px-2 py-1.5 rounded border border-[var(--border)] bg-[var(--surface)] text-[var(--text-1)] focus:outline-none focus:border-[var(--accent)] resize-none custom-scrollbar"
            />
            <div className="text-[11px] text-[var(--text-3)] text-right mt-0.5">
              {comment.length}/2000
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[var(--border)]">
          <button
            onClick={onClose}
            className="text-[12px] px-3 py-1.5 rounded text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-[var(--surface-2)] transition-colors"
          >
            {t('chat.feedback.modal.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="text-[12px] px-3 py-1.5 rounded bg-[#CC0000] text-white hover:opacity-90 transition-opacity"
          >
            {t('chat.feedback.modal.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
